import { useState, useEffect, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { Download, ChevronDown, Pencil } from "lucide-react";
import { CorrectionDialog } from "@/components/CorrectionDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n";
import { api, errMsg, fmtTime, fmtMin, fmtDate, todayStr, monthRange, downloadCsv } from "@/lib/api";
import { Card, Loader, PageTitle, GeoBadge, PositionBadge, CorrectedNote } from "@/components/kv";

const Details = ({ items, onEdit }) => {
  const { t, lang } = useT();
  if (!items.length) return <p className="text-sm text-stone-500 p-3">{t("report.no_data")}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="text-xs text-stone-500 text-left">
          <tr>
            <th className="p-2">{t("common.date")}</th><th className="p-2">{t("report.expected")}</th><th className="p-2">{t("dash.col_in")}</th>
            <th className="p-2">{t("dash.col_out")}</th><th className="p-2">{t("dash.col_loc")}</th><th className="p-2">{t("report.worked")}</th><th className="p-2">{t("report.overtime")}</th><th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-t border-[#E8DFD0]">
              <td className="p-2 capitalize">{fmtDate(r.date, lang)}</td>
              <td className="p-2 font-mono-kv">{r.expected_start ? `${r.expected_start}–${r.expected_end}` : t("status.off")}</td>
              <td className="p-2 font-mono-kv">{fmtTime(r.clock_in)}{r.is_late && <span className="ml-1 text-amber-800 text-xs">({t("dash.late_by", { m: fmtMin(r.late_min, t) })})</span>}</td>
              <td className="p-2 font-mono-kv">
                {r.open ? t("report.open") : fmtTime(r.clock_out)}
                {r.auto_closed && <div className="font-sans text-[11px] text-red-700 max-w-[160px]">{t("report.auto_closed")}</div>}
                <CorrectedNote r={r} />
              </td>
              <td className="p-2"><GeoBadge verified={r.in_verified} distance={r.in_distance_m} manual={r.manual} />
                {r.in_suspicious && !r.manual && <div className="mt-1"><span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[11px] font-semibold" data-testid={`report-suspicious-${r.id}`}>{t("settings.suspicious")}</span></div>}
              </td>
              <td className="p-2">{fmtMin(r.worked_min, t)}</td>
              <td className="p-2">
                {r.overtime_min > 0 ? (
                  <span className="rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
                    {t("report.ot_badge")} +{fmtMin(r.overtime_min, t)}
                  </span>
                ) : "—"}
                {(r.early_min > 0 || r.late_out_min > 0) && (
                  <div className="text-[11px] text-stone-500 mt-0.5">{t("report.early")}: {r.early_min} · {t("report.late_out")}: {r.late_out_min} {t("common.m")}</div>
                )}
              </td>
              <td className="p-2 text-right">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => onEdit(r)} data-testid={`report-edit-${r.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("req.edit")}</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Report() {
  const { t } = useT();
  const [start, setStart] = useState(monthRange(todayStr())[0]);
  const [end, setEnd] = useState(todayStr());
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState({});
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    if (!start || !end || end < start) return;
    api.get("/admin/report", { params: { start, end } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e, t)));
  }, [start, end, t]);
  useEffect(() => {
    setRows(null);
    load();
  }, [load]);

  const exportCsv = () => {
    const head = [t("common.name"), t("common.position"), t("common.date"), t("report.expected"), t("dash.col_in"), t("dash.col_out"), t("dash.col_loc"), `${t("report.worked")} (${t("common.m")})`, `${t("report.normal")} (${t("common.m")})`, `${t("report.overtime")} (${t("common.m")})`];
    const data = rows.flatMap((r) => r.items.map((i) => [r.user.name, t(`positions.${r.user.position}`), i.date, i.expected_start ? `${i.expected_start}-${i.expected_end}` : "-", fmtTime(i.clock_in), i.open ? "-" : fmtTime(i.clock_out), i.in_verified ? t("clock.verified") : t("clock.outside"), i.worked_min, i.normal_min, i.overtime_min]));
    downloadCsv(`mesai-raporu_${start}_${end}.csv`, [head, ...data]);
  };

  return (
    <div>
      <PageTitle kicker={t("adminnav.report")} title={t("report.title")}>
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40 bg-white rounded-full" data-testid="report-start-input" />
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40 bg-white rounded-full" data-testid="report-end-input" />
        <Button variant="outline" className="rounded-full" onClick={exportCsv} disabled={!rows} data-testid="report-export-button"><Download className="h-4 w-4 mr-1.5" />{t("common.export_csv")}</Button>
      </PageTitle>
      {!rows ? <Loader /> : (
        <Card className="overflow-x-auto" data-testid="report-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F3EDE2] text-left text-xs uppercase tracking-wider text-stone-600">
              <tr>
                <th className="p-3">{t("common.staff")}</th><th className="p-3">{t("report.days")}</th><th className="p-3">{t("report.total")}</th>
                <th className="p-3">{t("report.normal")}</th><th className="p-3">{t("report.overtime")}</th><th className="p-3">{t("report.late_count")}</th><th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.user.id}>
                  <tr className="border-t border-[#E8DFD0]" data-testid={`report-row-${r.user.username}`}>
                    <td className="p-3"><div className="font-semibold">{r.user.name}</div><PositionBadge position={r.user.position} /></td>
                    <td className="p-3 font-mono-kv">{r.totals.days}</td>
                    <td className="p-3 font-mono-kv">{fmtMin(r.totals.worked_min, t)}</td>
                    <td className="p-3 font-mono-kv">{fmtMin(r.totals.normal_min, t)}</td>
                    <td className="p-3 font-mono-kv font-bold text-orange-700" data-testid={`report-overtime-${r.user.username}`}>{fmtMin(r.totals.overtime_min, t)}</td>
                    <td className="p-3 font-mono-kv text-red-700">{r.totals.late_count}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setOpen((o) => ({ ...o, [r.user.id]: !o[r.user.id] }))} data-testid={`report-details-${r.user.username}`}>
                        {open[r.user.id] ? t("report.hide") : t("report.details")}
                        <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${open[r.user.id] ? "rotate-180" : ""}`} />
                      </Button>
                    </td>
                  </tr>
                  {open[r.user.id] && (
                    <tr><td colSpan={7} className="bg-[#FAF6F0] px-3 pb-3"><Details items={r.items} onEdit={(rec) => setEditing({ rec, name: r.user.name })} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {editing && <CorrectionDialog rec={editing.rec} name={editing.name} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
